import { MutableRefObject, useState } from 'react';
import Highcharts, { Chart, YAxisOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment-timezone';

import { TOOLTIP_STYLES, timeSeriesChartOptions } from 'modules/experiments/constants';
import {
  ExperimentVariantBreakdownDetail,
  VariantBreakdownCardProps,
} from 'modules/experiments/types';
import {
  useExperimentSearchParams,
  useExperimentsTranslations,
  useMetricsTranslations,
} from 'modules/experiments/hooks';
import {
  getFormattedDateWithWeekDayFromEpoch,
  getTimeSeriesData,
  isExperimentVariantBreakdownRow,
} from 'modules/experiments/utils';
import { ControlBadge } from 'modules/experiments/components/ui';
import { getChartSymbolAndColorByIndex, getHighchartSymbolsSvg } from 'utils/chartTsx';

import {
  ChartWrapper,
  PerformanceChartLegendItem,
  PerformanceChartLegendText,
  PerformanceChartLegendWrapper,
} from './Styled';

type TimeSeriesChartProps = VariantBreakdownCardProps & {
  chartRef: MutableRefObject<any>;
};

export const generateChartsTooltipMarkup = (
  variationName: string,
  currentValue: number,
  currentTime: number,
  timezone: string,
) => {
  const {
    wrapperTypography,
    wrapperAlignment,
    primaryValueTypography,
    categoryAlignment,
    secondaryValueTypography,
  } = TOOLTIP_STYLES;

  const displayTime = getFormattedDateWithWeekDayFromEpoch(currentTime, timezone);

  return `
    <div style="${wrapperTypography} ${wrapperAlignment}">
      <span style="${primaryValueTypography}">${currentValue}</span>
      <span style="${categoryAlignment}">${variationName}</span>
      <span style="${secondaryValueTypography}">(${displayTime})</span>
    </div>
  `;
};

export const TimeSeriesChart = ({ chartRef, variantList, metricMap }: TimeSeriesChartProps) => {
  const { t } = useExperimentsTranslations();
  const { t: mt, i18n } = useMetricsTranslations();
  const [chart, setChart] = useState<Chart>();
  const [selectedMetric] = useExperimentSearchParams().selectedPerformanceOverTimeMetric;
  const { timezone } = useExperimentSearchParams();
  const controlVariant = (
    variantList.filter(isExperimentVariantBreakdownRow) as ExperimentVariantBreakdownDetail[]
  ).find(({ control }) => control);
  /**
   * HighChart by default mutates the data that is passed to it.
   * Ref - https://github.com/highcharts/highcharts-react/issues/326
   *
   * This behavior can be turned off in v10 by setting the option
   * allowMutatingData to false. But, this is not recommended
   * for performance reason.
   * Ref - https://api.highcharts.com/highcharts/chart.allowMutatingData
   *
   * So, creating a copy of the variant list to be passed to the HighChart
   * This will keep all mutations local and prevent changes to the main variant list
   */
  const copyVariantList = JSON.parse(JSON.stringify(variantList));

  const options: Highcharts.Options = {
    ...timeSeriesChartOptions,
    tooltip: {
      ...timeSeriesChartOptions.tooltip,
      // eslint-disable-next-line
      pointFormatter: function () {
        const {
          y: currentValue = 0,
          x: currentTime,
          series: { name },
        } = this;
        return generateChartsTooltipMarkup(name, currentValue, currentTime, timezone);
      },
    },
    lang: {
      noData: t('chart.noData'),
    },
    legend: {
      // eslint-disable-next-line
      labelFormatter: function () {
        return this.name === controlVariant?.variantName
          ? `${this.name} (${t('badge.control')})`
          : this.name;
      },
    },
    // Adding graph data
    series: getTimeSeriesData(
      copyVariantList,
      selectedMetric,
      metricMap,
      t('detailsMetadata.holdout'),
    ),
    yAxis: {
      ...timeSeriesChartOptions.yAxis,
      title: {
        ...(timeSeriesChartOptions?.yAxis as YAxisOptions)?.title,
        // Setting the yAxis title to the selected metric
        text: i18n.exists(`metrics:${selectedMetric}`) ? mt(selectedMetric) : selectedMetric,
      },
    },
  };

  if (timezone) {
    // Higchart uses moment to deduce timezone offset.
    // @ts-ignore
    window.moment = moment;
    options.time = {
      timezone,
    };
  }

  return (
    <ChartWrapper>
      <HighchartsReact
        ref={chartRef}
        options={options}
        containerProps={{ style: { width: '100%' } }}
        callback={(chartState) =>
          // Update the chart state only if it's not set. If not the state gets reset
          // when the callback is run after data export to re-render the chart.
          setChart((oldChartState) => (!oldChartState ? chartState : oldChartState))
        }
      />
      {/* Showing custom xAxis legends */}
      <PerformanceChartLegendWrapper>
        {!!chart &&
          chart?.series?.map((line, index) => {
            const { symbol, color } = getChartSymbolAndColorByIndex(index);

            return (
              <PerformanceChartLegendItem
                key={`${line.name}-${symbol}-${color}`}
                data-test={`${symbol}`}
              >
                {getHighchartSymbolsSvg(symbol, { color })}
                <PerformanceChartLegendText>{line.name}</PerformanceChartLegendText>
                {/* We have set the control variant to selected in the chart */}
                {line.selected && <ControlBadge />}
              </PerformanceChartLegendItem>
            );
          })}
      </PerformanceChartLegendWrapper>
    </ChartWrapper>
  );
};
